import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Plus, Loader2, FileEdit, Trash2, Edit2, Calendar, Search, ExternalLink, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import ReferencesSection from '../../components/ReferencesSection';
import axios from 'axios';

import { API_BASE as API } from '../../config/api';

const TextsPage = () => {
  const { conlang , canEdit} = useOutletContext();
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedText, setSelectedText] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [conlangText, setConlangText] = useState('');
  const [translation, setTranslation] = useState('');
  const [notes, setNotes] = useState('');
  const [formRefs, setFormRefs] = useState([]);
  const [newRefUrl, setNewRefUrl] = useState('');
  const [newRefLabel, setNewRefLabel] = useState('');

  useEffect(() => {
    fetchTexts();
  }, [conlang?.conlang_id]);

  const fetchTexts = async () => {
    if (!conlang?.conlang_id) return;
    try {
      const response = await axios.get(`${API}/conlangs/${conlang.conlang_id}/texts`, {
        withCredentials: true
      });
      setTexts(response.data);
    } catch (error) {
      toast.error('Failed to load texts');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setConlangText('');
    setTranslation('');
    setNotes('');
    setFormRefs([]);
    setNewRefUrl('');
    setNewRefLabel('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const response = await axios.post(
        `${API}/conlangs/${conlang.conlang_id}/texts`,
        { title, conlang_text: conlangText, translation, notes, references: formRefs },
        { withCredentials: true }
      );
      setTexts([response.data, ...texts]);
      setCreateDialogOpen(false);
      resetForm();
      toast.success('Text created!');
    } catch (error) {
      toast.error('Failed to create text');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedText) return;
    setFormLoading(true);
    try {
      const response = await axios.put(
        `${API}/conlangs/${conlang.conlang_id}/texts/${selectedText.text_id}`,
        { title, conlang_text: conlangText, translation, notes, references: formRefs },
        { withCredentials: true }
      );
      setTexts(texts.map(t => t.text_id === selectedText.text_id ? response.data : t));
      setEditDialogOpen(false);
      setSelectedText(null);
      resetForm();
      toast.success('Text updated!');
    } catch (error) {
      toast.error('Failed to update text');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedText) return;
    setFormLoading(true);
    try {
      await axios.delete(
        `${API}/conlangs/${conlang.conlang_id}/texts/${selectedText.text_id}`,
        { withCredentials: true }
      );
      setTexts(texts.filter(t => t.text_id !== selectedText.text_id));
      setDeleteDialogOpen(false);
      setSelectedText(null);
      toast.success('Text deleted');
    } catch (error) {
      toast.error('Failed to delete text');
    } finally {
      setFormLoading(false);
    }
  };

  const openEditDialog = (text) => {
    setSelectedText(text);
    setTitle(text.title);
    setConlangText(text.conlang_text);
    setTranslation(text.translation || '');
    setNotes(text.notes || '');
    setFormRefs(text.references || []);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (text) => {
    setSelectedText(text);
    setDeleteDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric' 
    });
  };

  const filteredTexts = texts.filter(t => 
    t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.conlang_text.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.translation && t.translation.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="texts-title">
            Texts
          </h2>
          <p className="text-foreground-muted mt-1">
            Sample texts and translations in {conlang?.name}
          </p>
        </div>
        {canEdit && (
        <Button 
          onClick={() => { resetForm(); setCreateDialogOpen(true); }}
          className="bg-primary hover:bg-primary/90"
          data-testid="create-text-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Text
        </Button>
        )}
      </div>

      {/* Search */}
      {texts.length > 0 && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-subtle" />
          <Input
            placeholder="Search texts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-background-paper border-border"
            data-testid="search-texts-input"
          />
        </div>
      )}

      {/* Texts List */}
      {filteredTexts.length === 0 ? (
        <Card className="bg-background-paper border-border">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <FileEdit className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
              {texts.length === 0 ? 'No texts yet' : 'No matching texts'}
            </h3>
            <p className="text-foreground-muted mb-4">
              {texts.length === 0 
                ? 'Add sample texts to showcase your conlang in use.'
                : 'Try a different search term.'
              }
            </p>
            {texts.length === 0 && (
              <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Text
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTexts.map((text) => (
            <Card 
              key={text.text_id} 
              className="bg-background-paper border-border hover:border-primary/30 transition-colors"
              data-testid={`text-card-${text.text_id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="font-heading text-lg text-foreground">
                    {text.title}
                  </CardTitle>
                  {canEdit && (
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => openEditDialog(text)}
                      data-testid={`edit-text-${text.text_id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => openDeleteDialog(text)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-text-${text.text_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Conlang Text */}
                <div className="p-4 bg-accent/30 rounded-lg">
                  <p className="font-conlang text-lg text-foreground leading-relaxed whitespace-pre-wrap">
                    {text.conlang_text}
                  </p>
                </div>
                
                {/* Translation */}
                {text.translation && (
                  <div className="p-4 bg-background rounded-lg border border-border">
                    <p className="text-sm text-foreground-muted italic leading-relaxed whitespace-pre-wrap">
                      {text.translation}
                    </p>
                  </div>
                )}
                
                {/* Notes */}
                {text.notes && (
                  <p className="text-sm text-foreground-subtle">
                    {text.notes}
                  </p>
                )}
                
                {/* Metadata */}
                <div className="flex items-center gap-2 text-xs text-foreground-subtle">
                  <Calendar className="w-3 h-3" />
                  <span>Added {formatDate(text.created_at)}</span>
                </div>
                {/* References */}
                {text.references?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {text.references.map((ref, i) => (
                      <span key={i} className="text-xs text-foreground-subtle inline-flex items-center gap-1">
                        {ref.url ? (
                          <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">
                            <BookOpen className="w-3 h-3" />{ref.label || ref.url}<ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-0.5"><BookOpen className="w-3 h-3" />{ref.label}</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Add Text</DialogTitle>
            <DialogDescription className="text-foreground-muted">
              Add a sample text in {conlang?.name} with translation.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-foreground">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Greeting, Short Story, Poem..."
                className="bg-background border-border"
                required
                data-testid="text-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="conlangText" className="text-foreground">Text in {conlang?.name}</Label>
              <Textarea
                id="conlangText"
                value={conlangText}
                onChange={(e) => setConlangText(e.target.value)}
                placeholder="Enter text in your conlang..."
                className="bg-background border-border min-h-[100px] font-conlang"
                required
                data-testid="text-conlang-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="translation" className="text-foreground">Translation (optional)</Label>
              <Textarea
                id="translation"
                value={translation}
                onChange={(e) => setTranslation(e.target.value)}
                placeholder="English translation..."
                className="bg-background border-border min-h-[80px]"
                data-testid="text-translation-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-foreground">Notes (optional)</Label>
              <Input
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional context..."
                className="bg-background border-border"
                data-testid="text-notes-input"
              />
            </div>
            {/* Inline References */}
            <div className="space-y-2 border-t border-border pt-3">
              <Label className="text-foreground text-sm">References</Label>
              {formRefs.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="flex-1 truncate text-foreground-muted">
                    {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{r.label || r.url}</a>}
                    {!r.url && r.label}
                  </span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setFormRefs(formRefs.filter((_, j) => j !== i))} className="text-destructive h-6 px-1">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input value={newRefUrl} onChange={e => setNewRefUrl(e.target.value)} placeholder="URL" className="bg-background border-border text-xs h-7 flex-1" />
                <Input value={newRefLabel} onChange={e => setNewRefLabel(e.target.value)} placeholder="Description" className="bg-background border-border text-xs h-7 flex-1" />
                <Button type="button" size="sm" variant="outline" className="h-7 px-2" disabled={!newRefUrl && !newRefLabel}
                  onClick={() => { setFormRefs([...formRefs, { ...(newRefUrl && { url: newRefUrl }), ...(newRefLabel && { label: newRefLabel }) }]); setNewRefUrl(''); setNewRefLabel(''); }}>
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading || !title.trim() || !conlangText.trim()}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Text'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Edit Text</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title" className="text-foreground">Title</Label>
              <Input
                id="edit-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="bg-background border-border"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-conlangText" className="text-foreground">Text in {conlang?.name}</Label>
              <Textarea
                id="edit-conlangText"
                value={conlangText}
                onChange={(e) => setConlangText(e.target.value)}
                className="bg-background border-border min-h-[100px] font-conlang"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-translation" className="text-foreground">Translation</Label>
              <Textarea
                id="edit-translation"
                value={translation}
                onChange={(e) => setTranslation(e.target.value)}
                className="bg-background border-border min-h-[80px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-notes" className="text-foreground">Notes</Label>
              <Input
                id="edit-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-background border-border"
              />
            </div>
            {/* Inline References */}
            <div className="space-y-2 border-t border-border pt-3">
              <Label className="text-foreground text-sm">References</Label>
              {formRefs.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="flex-1 truncate text-foreground-muted">
                    {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{r.label || r.url}</a>}
                    {!r.url && r.label}
                  </span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setFormRefs(formRefs.filter((_, j) => j !== i))} className="text-destructive h-6 px-1">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input value={newRefUrl} onChange={e => setNewRefUrl(e.target.value)} placeholder="URL" className="bg-background border-border text-xs h-7 flex-1" />
                <Input value={newRefLabel} onChange={e => setNewRefLabel(e.target.value)} placeholder="Description" className="bg-background border-border text-xs h-7 flex-1" />
                <Button type="button" size="sm" variant="outline" className="h-7 px-2" disabled={!newRefUrl && !newRefLabel}
                  onClick={() => { setFormRefs([...formRefs, { ...(newRefUrl && { url: newRefUrl }), ...(newRefLabel && { label: newRefLabel }) }]); setNewRefUrl(''); setNewRefLabel(''); }}>
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading || !title.trim() || !conlangText.trim()}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-background-paper border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Delete Text</DialogTitle>
            <DialogDescription className="text-foreground-muted">
              Are you sure you want to delete "{selectedText?.title}"? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={formLoading}>
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ReferencesSection section="texts" />
    </div>
  );
};

export default TextsPage;
